import { Injectable } from '@angular/core';
import { StorageService } from '../storage-service/storage.service';
import * as moment from 'moment';
import { EventLogModel } from "../config-eventlogs/eventlog-model";

@Injectable()
export class IndexedDBService {
  private db: IDBDatabase;
  private logId: number = 0;
  private LOGS_LENGTH: number = 1000;
  private LOGS_TABLE_NAME: string = 'logs';
  private LOGS_DB_PREFIX: string = 'WBM_DB_';
  resultArray: Array<EventLogModel> = [];

  constructor(private _storage: StorageService) { }

  private initWBMDatabase(): Promise<string> {

    return new Promise<string>((resolve) => {
      let request = indexedDB.open(this.LOGS_DB_PREFIX + this._storage.getHost());

      request.onupgradeneeded = function (event) {
        // The database did not previously exist, so create object stores and indexes.
        this.db = event.target.result;
        let objectStore = this.db.createObjectStore(this.LOGS_TABLE_NAME, { keyPath: 'id' });
        // Use transaction oncomplete to make sure the objectStore creation is
        // finished before adding data into it.
        objectStore.transaction.oncomplete = function () {
          resolve();
        };
      }.bind(this);

      request.onsuccess = function () {
        this.db = request.result;
        resolve();
      }.bind(this);
    });
  }

  private storeInLogsStore(date: Date, tag: string, message: string, type: string) {
    this.initWBMDatabase()
      .then(() => {
        let tx = this.db.transaction(this.LOGS_TABLE_NAME, 'readwrite');
        let store = tx.objectStore(this.LOGS_TABLE_NAME);
        const safeMessage = this.safeSerialize(message);
        store.put({ id: this.logId++ % this.LOGS_LENGTH, date: date.toJSON(), tag, message: safeMessage, type });
      });
  }

  private safeSerialize(value: any): any {
    try {
      if (value === null || value === undefined) { return value; }
      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') { return value; }

      if (value instanceof Error) {
        return { error: true, name: value.name, message: value.message, stack: value.stack };
      }

      if (value instanceof Headers || value && typeof value.get === 'function' && typeof value.keys === 'function') {
        try {
          const obj: any = {};
          if (typeof value.forEach === 'function') {
            value.forEach((v: any, k: any) => obj[k] = v);
          } else if (typeof value.keys === 'function') {
            for (const k of value.keys()) { obj[k] = value.get(k); }
          }
          return { headers: obj };
        } catch (e) {
          return String(value);
        }
      }

      if (value instanceof Map) {
        const obj: any = {};
        value.forEach((v: any, k: any) => obj[String(k)] = this.safeSerialize(v));
        return { map: obj };
      }
      if (value instanceof Set) {
        return { set: Array.from(value).map(v => this.safeSerialize(v)) };
      }

      if (Array.isArray(value)) {
        return value.map(v => this.safeSerialize(v));
      }

      if (typeof value === 'object') {
        const out: any = {};
        for (const k of Object.keys(value)) {
          const v = value[k];
          if (typeof v === 'function') { out[k] = '[Function]'; continue; }
          out[k] = this.safeSerialize(v);
        }
        return out;
      }
      return String(value);
    } catch (e) {
      return String(value);
    }
  }

  private retrieveLogsStore(): Promise<string> {
    return new Promise((resolve) => {
      let logArray: Array<Object> = [];

      let transaction: IDBTransaction = this.db.transaction(this.LOGS_TABLE_NAME, 'readonly');
      let objectStore: IDBObjectStore = transaction.objectStore(this.LOGS_TABLE_NAME);

      let request = objectStore.openCursor();
      request.onsuccess = function () {
        let cursor = request.result;
        if (cursor) {
          logArray.push(cursor.value);
          cursor.continue();
        } else {
          let sortedLogs: Array<string> = logArray.sort((a, b) => a['date'].localeCompare(b['date']))
            .map((log: Object) => moment(log['date']).format('DD/MM HH:mm:ss.sss') + ' ' + log['type'] + ' ' + log['tag'] + ' ' + log['message']);

          resolve(sortedLogs.join('\n'));
        }
      };
    });
  }

  public storeInfo(date: Date, tag: string, message: string) {
    this.storeInLogsStore(date, tag, message, 'Info');
  }

  public storeLog(date: Date, tag: string, message: string) {
    this.storeInLogsStore(date, tag, message, 'Log');
  }

  public storeWarn(date: Date, tag: string, message: string) {
    this.storeInLogsStore(date, tag, message, 'Warn');
  }

  public storeError(date: Date, tag: string, message: string) {
    this.storeInLogsStore(date, tag, message, 'Error');
  }

  public retrieveLogsStoreIfCreated(): Promise<string> {
    return this.initWBMDatabase()
      .then(this.retrieveLogsStore.bind(this));
  }

}
